import type { RefObject } from 'react'
import { useCallback } from 'react'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

/**
 * Decides where composer focus lands once a message has been sent or a voice
 * transcript has been added.
 *
 * A soft keyboard stays up for as long as the textarea keeps focus, covering the
 * reply or transcript the traveller wants to read, so every chat composer blurs on
 * the devices that have one. This mirrors the predicate the Trip Chat surface router uses to
 * pick the mobile shell, so a tablet on the mobile surface behaves like a phone.
 * Everywhere else keeps focus so the next message can be typed straight away.
 *
 * @param textareaRef the composer textarea to settle focus on
 * @param forceDismissKeyboard blurs on every device, for surfaces that never want
 * focus returned regardless of where they are rendered
 */
export function useSettleComposerFocus(
  textareaRef: RefObject<HTMLTextAreaElement | null>,
  forceDismissKeyboard = false,
): () => void {
  const isMobileOrTablet = useIsMobileOrTablet()
  const isMobileViewport = useScreenSizeOnly('mobile')
  const hasSoftKeyboard = isMobileOrTablet || isMobileViewport

  return useCallback(() => {
    if (forceDismissKeyboard || hasSoftKeyboard) {
      textareaRef.current?.blur()
      return
    }

    textareaRef.current?.focus()
  }, [forceDismissKeyboard, hasSoftKeyboard, textareaRef])
}
